import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withUploadToken } from '../../utils/fileUrl';
import { hasActionPermission } from '../../utils/accessControl';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Horse } from '@phosphor-icons/react';
import { exportStyledWorkbook } from '../../utils/xlsxStyled';
import api, { apiBaseURL } from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import { formatDateDisplay, formatDateTimeInput } from '../../utils/dateUtils';
import '../../App.css';

const intFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const isNotDeleted = (horse) => String(horse?.is_deleted ?? '0') === '0';

function formatNumber(value) {
  return intFormatter.format(Number(value || 0));
}

function formatDate(value) {
  return formatDateDisplay(value);
}

function calculateAge(value) {
  if (!value) return '-';
  const birth = new Date(value);
  if (Number.isNaN(birth.getTime())) return '-';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return Math.max(age, 0);
}

function formatHorseName(horseName) {
  const match = horseName?.match(/(.*)\(([^)]+)\)$/);
  if (!match) return { name: horseName || '-', code: '' };
  return { name: match[1].trim(), code: match[2].trim() };
}

function formatDisplayName(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  if (!raw.includes(' ') && raw.length <= 4) return raw.toUpperCase();

  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((part) => {
      if (!part) return part;
      if (/^[a-z]{1,3}$/i.test(part) && ['el', 'al'].includes(part)) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function getHorseName(horse, language) {
  if (language === 'ar') return horse.name_arabic || formatDisplayName(horse.name);
  const { name, code } = formatHorseName(horse.name);
  const displayName = formatDisplayName(name);
  return code ? `${displayName} (${code})` : displayName;
}

function getImageSrc(image) {
  if (!image) return undefined;
  if (image.startsWith('data') || image.startsWith('http')) return image;
  return withUploadToken(`${apiBaseURL}/uploads/${image}`);
}

function makeMap(rows, idKey, arKey, enKey, language, fallbackKey) {
  return (rows || []).reduce((acc, row) => {
    acc[row[idKey]] =
      language === 'ar'
        ? row[arKey] || row[enKey] || row[fallbackKey] || '-'
        : row[enKey] || row[arKey] || row[fallbackKey] || '-';
    return acc;
  }, {});
}

// Horse management actions (add / import / export / template) are gated by the
// customizable `horse-manage` permission (grantable per role from the Access
// Control center) instead of a hardcoded role. System Admin bypasses.
function canManageHorses() {
  return hasActionPermission('horse-manage');
}

function ResponsiveGrid({ children, min = 220, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: 2,
        alignItems: 'stretch',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar
          sx={{
            bgcolor: soft,
            color,
            width: 44,
            height: 44,
            mt: 0.2,
            flexShrink: 0,
            '& svg': {
              display: 'block',
              width: 24,
              height: 24,
              transform: 'translateY(-1px)',
            },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function SortLabel({ children, field, sortField, sortDirection, onClick }) {
  const active = sortField === field;
  return (
    <Button
      size="small"
      onClick={() => onClick(field)}
      sx={{
        color: '#fff',
        fontWeight: 900,
        px: 0.5,
        minWidth: 0,
        textTransform: 'none',
        '&:hover': {
          bgcolor: 'rgba(255, 255, 255, 0.12)',
          color: '#fff',
        },
      }}
    >
      {children} {active ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
    </Button>
  );
}

export default function HorseList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const initialSort = new URLSearchParams(location.search).get('sort') === 'latest';
  const language = i18n.language === 'ar' ? 'ar' : 'en';
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  const [horses, setHorses] = useState([]);
  const [owners, setOwners] = useState({});
  const [colors, setColors] = useState({});
  const [breeds, setBreeds] = useState({});
  const [countries, setCountries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [sortField, setSortField] = useState(initialSort ? 'created_at' : 'horse_id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const genders = useMemo(
    () => ({
      S: language === 'ar' ? 'حصان' : 'Stallion',
      M: language === 'ar' ? 'فرس' : 'Mare',
    }),
    [language]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [horsesRes, ownersRes, colorsRes, breedsRes, countriesRes] =
        await Promise.all([
          api.get('/horseslist', { params: { is_deleted: 0, is_active: 1 } }),
          api.get('/owners/summary'),
          api.get('/horse_colors'),
          api.get('/horse_breeds'),
          api.get('/countries'),
        ]);

      const horseRows = Array.isArray(horsesRes.data) ? horsesRes.data : [];
      setHorses(horseRows.filter(isNotDeleted));
      setOwners(
        (ownersRes.data || []).reduce((acc, owner) => {
          acc[owner.owner_id] =
            owner.name ||
            [owner.first_name, owner.last_name].filter(Boolean).join(' ') ||
            '-';
          return acc;
        }, {})
      );
      setColors(makeMap(colorsRes.data, 'color_id', 'color_arabic', 'color_english', language, 'color_name'));
      setBreeds(makeMap(breedsRes.data, 'breed_id', 'breed_arabic', 'breed_english', language, 'breed_name'));
      setCountries(makeMap(countriesRes.data, 'country_id', 'country_arabic', 'country_english', language, 'country_name'));
    } catch (err) {
      setError(t('error_fetching_horses') || 'Failed to fetch horses');
    } finally {
      setLoading(false);
    }
  }, [language, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const stallions = horses.filter((horse) => horse.gender === 'S').length;
    const mares = horses.filter((horse) => horse.gender === 'M').length;
    const withRfid = horses.filter((horse) => horse.rfid_chip_number).length;
    const withCertificate = horses.filter(
      (horse) => horse.registration_certificate || horse.has_registration_certificate === 'Y'
    ).length;
    return { stallions, mares, withRfid, withCertificate };
  }, [horses]);

  const filteredHorses = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return horses
      .filter((horse) => (genderFilter ? horse.gender === genderFilter : true))
      .filter((horse) => {
        if (!needle) return true;
        return [
          horse.name,
          horse.name_arabic,
          horse.rfid_chip_number,
          owners[horse.owner_id],
          breeds[horse.breed_id],
          colors[horse.color_id],
          horse.country_arabic,
          horse.country_english,
          countries[horse.country_of_origin_id],
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      });
  }, [breeds, colors, countries, genderFilter, horses, owners, searchTerm]);

  const sortedHorses = useMemo(() => {
    const rows = [...filteredHorses];
    rows.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'age') {
        aValue = calculateAge(a.date_of_birth);
        bValue = calculateAge(b.date_of_birth);
      }
      if (sortField === 'owner_id') {
        aValue = owners[a.owner_id] || '';
        bValue = owners[b.owner_id] || '';
      }
      if (sortField === 'breed_id') {
        aValue = breeds[a.breed_id] || '';
        bValue = breeds[b.breed_id] || '';
      }
      if (sortField === 'color_id') {
        aValue = colors[a.color_id] || '';
        bValue = colors[b.color_id] || '';
      }
      if (sortField === 'created_at') {
        aValue = a.created_at ? new Date(a.created_at).getTime() : Number(a.horse_id || 0);
        bValue = b.created_at ? new Date(b.created_at).getTime() : Number(b.horse_id || 0);
      }

      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return rows;
  }, [breeds, colors, filteredHorses, owners, sortDirection, sortField]);

  const pagedHorses = sortedHorses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const downloadTemplate = async () => {
    // عبر axios (يُرفق التوكن في Authorization) بدل window.open الذي لا يرسله
    // ⇒ كان يفشل بـ "No token provided" بعد تفعيل بوابة المصادقة.
    try {
      const res = await api.get('/horses/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'horse_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err?.message || err);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    setImportResult(null);
    setError('');

    try {
      const { data: result } = await api.post('/horses/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(result);
      await loadData();
    } catch (err) {
      const payload = err?.response?.data;
      if (payload?.errors || payload?.message) {
        setImportResult(payload);
      } else {
        setError(language === 'ar' ? 'تعذر استيراد ملف الخيل' : 'Failed to import horses file');
      }
    } finally {
      setImporting(false);
    }
  };

  const exportToExcel = () => {
    // Build a clean, language-agnostic row shape. The shared styled exporter
    // gives us the gold banner / borders / alternating fills / freeze + filter
    // for free — no manual sheet plumbing needed here.
    const exportData = sortedHorses.map((horse) => ({
      ID: horse.horse_id,
      'اسم الخيل بالعربي': horse.name_arabic || '-',
      'Horse Name': horse.name || '-',
      RFID: horse.rfid_chip_number || '-',
      'Date of Birth': formatDate(horse.date_of_birth),
      Age: calculateAge(horse.date_of_birth),
      Gender: genders[horse.gender] || '-',
      Owner: owners[horse.owner_id] || '-',
      Breed: breeds[horse.breed_id] || '-',
      Color: colors[horse.color_id] || '-',
      'Country of Origin':
        (language === 'ar'
          ? horse.country_arabic || horse.country_english
          : horse.country_english || horse.country_arabic) ||
        countries[horse.country_of_origin_id] ||
        '-',
      'Registration Number': horse.registration_number || '-',
    }));

    const stamp = formatDateTimeInput(new Date()).replace(/[:T]/g, '-');
    exportStyledWorkbook({
      fileName: `horses_list_${stamp}.xlsx`,
      sheetName: language === 'ar' ? 'قائمة الخيل' : 'Horse list',
      rows: exportData,
      subtitle:
        language === 'ar'
          ? `قائمة الخيل · ${sortedHorses.length} خيل · ${new Date().toLocaleDateString('ar-EG')}`
          : `Horse list · ${sortedHorses.length} horses · ${new Date().toLocaleDateString()}`,
    });
  };

  return (
    <Box dir={dir} sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1540, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            gap={2}
          >
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('horse_details') || 'Horse list'}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {language === 'ar'
                  ? 'إدارة الخيل المسجلة مع بحث سريع وفرز وتصدير.'
                  : 'Manage registered horses with search, sorting, and export.'}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              {/* Horse management actions — visible only to roles in
                  HORSE_MANAGEMENT_ROLE_IDS (currently: Stable Manager).
                  Wrapped in a single guard so the empty Stack collapses
                  gracefully for non-privileged users. */}
              {canManageHorses() && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleImportFile}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                    sx={{
                      borderColor: COLORS.line,
                      color: COLORS.text,
                      fontWeight: 800,
                      '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft },
                    }}
                  >
                    {language === 'ar' ? 'تحميل القالب' : 'Template'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    sx={{
                      borderColor: COLORS.line,
                      color: COLORS.text,
                      fontWeight: 800,
                      '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft },
                    }}
                  >
                    {importing
                      ? language === 'ar'
                        ? 'جاري الاستيراد...'
                        : 'Importing...'
                      : language === 'ar'
                      ? 'استيراد Excel'
                      : 'Import Excel'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportToExcel}
                    sx={{
                      borderColor: COLORS.line,
                      color: COLORS.text,
                      fontWeight: 800,
                      '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft },
                    }}
                  >
                    {t('export_to_excel') || 'Export'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/register-horse')}
                    sx={{
                      bgcolor: COLORS.gold,
                      fontWeight: 900,
                      '&:hover': { bgcolor: COLORS.goldHover },
                    }}
                  >
                    {t('register_horse') || 'Register horse'}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}
        {importResult ? (
          <Alert
            severity={Number(importResult.inserted_count || 0) > 0 ? 'success' : 'warning'}
            sx={{ mb: 2.5 }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              {language === 'ar'
                ? `تم إدخال ${importResult.inserted_count || 0} خيل. الأخطاء: ${importResult.error_count || 0}`
                : `Imported ${importResult.inserted_count || 0} horses. Errors: ${importResult.error_count || 0}`}
            </Typography>
            {Array.isArray(importResult.errors) && importResult.errors.length > 0 ? (
              <Box component="ul" sx={{ m: 0.5, pl: 2.5 }}>
                {importResult.errors.slice(0, 8).map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
                {importResult.errors.length > 8 ? (
                  <li>
                    {language === 'ar'
                      ? `و ${importResult.errors.length - 8} أخطاء أخرى`
                      : `and ${importResult.errors.length - 8} more errors`}
                  </li>
                ) : null}
              </Box>
            ) : null}
          </Alert>
        ) : null}

        <ResponsiveGrid min={220} sx={{ mb: 2.5 }}>
          <MetricCard icon={<Horse size={26} weight="fill" />} title={t('total_horses') || 'Total horses'} value={formatNumber(horses.length)} helper={`${formatNumber(filteredHorses.length)} ${t('shown') || 'shown'}`} />
          <MetricCard icon={<MaleIcon />} title={t('stallions') || 'Stallions'} value={formatNumber(stats.stallions)} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<FemaleIcon />} title={t('mares') || 'Mares'} value={formatNumber(stats.mares)} color={COLORS.rose} soft={COLORS.roseSoft} />
          <MetricCard icon={<Horse size={26} weight="fill" />} title={language === 'ar' ? 'لديها RFID' : 'With RFID'} value={formatNumber(stats.withRfid)} color={COLORS.green} soft={COLORS.greenSoft} />
        </ResponsiveGrid>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'visible' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.25} justifyContent="space-between">
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25}>
                <TextField
                  size="small"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setPage(0);
                  }}
                  placeholder={t('search_by_horse_or_owner') || 'Search horse or owner'}
                  sx={{ minWidth: { xs: '100%', sm: 340 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  label={t('gender') || 'Gender'}
                  value={genderFilter}
                  onChange={(event) => {
                    setGenderFilter(event.target.value);
                    setPage(0);
                  }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">{t('all') || 'All'}</MenuItem>
                  <MenuItem value="S">{genders.S}</MenuItem>
                  <MenuItem value="M">{genders.M}</MenuItem>
                </TextField>
              </Stack>
              <Chip
                label={`${formatNumber(sortedHorses.length)} ${language === 'ar' ? 'سجل' : 'records'}`}
                sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800, alignSelf: { xs: 'flex-start', md: 'center' } }}
              />
            </Stack>
          </Box>

          <TableContainer sx={smartTableContainerSx}>
            <Table size="small" sx={smartTableSx}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell width={70}>#</TableCell>
                  <TableCell>
                    <SortLabel field="name" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('horse_name') || 'Horse'}
                    </SortLabel>
                  </TableCell>
                  <TableCell align="center" width={120}>
                    <SortLabel field="gender" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('gender') || 'Gender'}
                    </SortLabel>
                  </TableCell>
                  <TableCell align="center" width={125}>
                    <SortLabel field="date_of_birth" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('date_of_birth') || 'DOB'}
                    </SortLabel>
                  </TableCell>
                  <TableCell align="center" width={90}>
                    <SortLabel field="age" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('age') || 'Age'}
                    </SortLabel>
                  </TableCell>
                  <TableCell>
                    <SortLabel field="owner_id" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('owner_name') || 'Owner'}
                    </SortLabel>
                  </TableCell>
                  <TableCell>
                    <SortLabel field="breed_id" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('breed') || 'Breed'}
                    </SortLabel>
                  </TableCell>
                  <TableCell>
                    <SortLabel field="color_id" sortField={sortField} sortDirection={sortDirection} onClick={handleSort}>
                      {t('color_id') || 'Color'}
                    </SortLabel>
                  </TableCell>
                  <TableCell align="center" width={120}>{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : pagedHorses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: COLORS.muted }}>
                      {t('no_data') || 'No data'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedHorses.map((horse, index) => (
                    <TableRow
                      key={horse.horse_id}
                      hover
                      sx={{
                        '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` },
                        '&:hover': { bgcolor: '#fffaf0' },
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ minWidth: 310 }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '46px minmax(0, 1fr)',
                            columnGap: 1.4,
                            alignItems: 'center',
                            width: '100%',
                            textAlign: language === 'ar' ? 'right' : 'left',
                          }}
                        >
                          <Avatar
                            src={getImageSrc(horse.image)}
                            alt={horse.name}
                            sx={{
                              width: 46,
                              height: 46,
                              bgcolor: COLORS.goldSoft,
                              color: COLORS.gold,
                              fontWeight: 900,
                              justifySelf: 'center',
                            }}
                          >
                            {getHorseName(horse, language).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0, width: '100%', justifySelf: 'stretch' }}>
                            <Typography
                              noWrap
                              onClick={() => navigate(`/horse-details/${horse.horse_id}`)}
                              sx={{
                                color: COLORS.text,
                                fontWeight: 900,
                                cursor: 'pointer',
                                display: 'block',
                                width: '100%',
                                textAlign: language === 'ar' ? 'right' : 'left',
                                direction: language === 'ar' ? 'rtl' : 'ltr',
                              }}
                            >
                              {getHorseName(horse, language)}
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.6}
                              sx={{
                                mt: 0.2,
                                justifyContent: language === 'ar' ? 'flex-end' : 'flex-start',
                                textAlign: language === 'ar' ? 'right' : 'left',
                                width: '100%',
                              }}
                            >
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: COLORS.muted, fontWeight: 800 }}
                              >
                                RFID
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: COLORS.muted, direction: 'ltr' }}
                              >
                                {horse.rfid_chip_number || '-'}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          icon={horse.gender === 'S' ? <MaleIcon /> : <FemaleIcon />}
                          label={genders[horse.gender] || '-'}
                          sx={{ bgcolor: horse.gender === 'S' ? COLORS.blueSoft : COLORS.roseSoft, color: COLORS.text, fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell align="center"><span className="date-ltr">{formatDate(horse.date_of_birth)}</span></TableCell>
                      <TableCell align="center">{calculateAge(horse.date_of_birth)}</TableCell>
                      <TableCell>{owners[horse.owner_id] || '-'}</TableCell>
                      <TableCell>{breeds[horse.breed_id] || '-'}</TableCell>
                      <TableCell>{colors[horse.color_id] || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('view_details') || 'View'}>
                          <IconButton onClick={() => navigate(`/horse-details/${horse.horse_id}`)} sx={{ color: COLORS.blue }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('edit') || 'Edit'}>
                          <IconButton onClick={() => navigate(`/horse-edit/${horse.horse_id}`)} sx={{ color: COLORS.gold }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedHorses.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
}
